/**
 * Подписи и правила банд (docs/team.md).
 *
 * Бэкенд не отдаёт ни одной готовой человеческой строки: роли, статусы,
 * типы операций казны, действия аудита и коды ошибок приходят машинными
 * ключами — все подписи собираются здесь.
 *
 * Переводы на 9 языков живут рядом с feature в `features/team/i18n`.
 * Русские `defaultValue` сохранены в вызовах как читаемый слой совместимости;
 * полноту ресурсов и интерполяций проверяет `npm run i18n:check`.
 */

import { formatSecondsLeft } from '../../../utils/formatters'
import { modeLabel } from './teamStats'

/** Иерархия ролей: чем больше, тем больше прав */
export const ROLE_RANK = { owner: 3, deputy: 2, moderator: 1, member: 0 }

export const roleRank = (role) => ROLE_RANK[role] ?? -1

export const roleLabel = (role, t) => {
  const labels = {
    owner: t('team.roles.owner', 'Создатель'),
    deputy: t('team.roles.deputy', 'Заместитель'),
    moderator: t('team.roles.moderator', 'Модератор'),
    member: t('team.roles.member', 'Участник')
  }
  return labels[role] || role
}

/** Цвет роли — бейдж в составе; создатель золотой, зам акцентный, модератор голубой */
export const roleBadgeClass = (role) => {
  const map = {
    owner: 'border-yellow-400/40 bg-yellow-400/10 text-yellow-300',
    deputy: 'border-accent-primary/40 bg-accent-primary/10 text-accent-primary',
    moderator: 'border-info/40 bg-info/10 text-info',
    member: 'border-white/10 bg-white/5 text-text-secondary'
  }
  return map[role] || map.member
}

// ---------------------------------------------------------------------------
// Права (зеркалят проверки бэкенда — UI просто не показывает недоступное)
// ---------------------------------------------------------------------------

/** Настройки, цвета, аватар, фон — создатель и заместитель */
export const canEditTeam = (role) => role === 'owner' || role === 'deputy'

/** Объявление лидера — только создатель */
export const canEditAnnouncement = (role) => role === 'owner'

/** Приём и отказ заявок — модератор и выше */
export const canManageApplications = (role) => roleRank(role) >= ROLE_RANK.moderator

/** Выплаты из казны — создатель и заместитель */
export const canPayTreasury = (role) => role === 'owner' || role === 'deputy'

/** Кик и смена роли — только тех, кто ниже тебя (и сам ты минимум модератор) */
export const canModerateMember = (myRole, targetRole) => (
  roleRank(myRole) >= ROLE_RANK.moderator && roleRank(myRole) > roleRank(targetRole)
)

/**
 * Какие роли можно выдать участнику: зама назначает только создатель,
 * модератора — создатель и зам; понижение до участника — как кик по правам.
 */
export const assignableRoles = (myRole, targetRole) => {
  if (!canModerateMember(myRole, targetRole)) return []
  const roles = []
  if (myRole === 'owner') roles.push('deputy')
  if (myRole === 'owner' || myRole === 'deputy') roles.push('moderator')
  roles.push('member')
  return roles.filter((role) => role !== targetRole)
}

// ---------------------------------------------------------------------------
// Статусы
// ---------------------------------------------------------------------------

export const teamStatusLabel = (status, t) => {
  const labels = {
    active: t('team.status.active', 'Активна'),
    deleting: t('team.status.deleting', 'Удаляется'),
    frozen: t('team.status.frozen', 'Заморожена')
  }
  return labels[status] || status
}

export const applicationStatusLabel = (status, t) => {
  const labels = {
    pending: t('team.applicationStatus.pending', 'На рассмотрении'),
    accepted: t('team.applicationStatus.accepted', 'Принята'),
    rejected: t('team.applicationStatus.rejected', 'Отклонена'),
    withdrawn: t('team.applicationStatus.withdrawn', 'Отозвана'),
    expired: t('team.applicationStatus.expired', 'Истекла'),
    auto_rejected: t('team.applicationStatus.autoRejected', 'Погашена автоматически')
  }
  return labels[status] || status
}

/** Цвет плашки статуса заявки */
export const applicationStatusClass = (status) => {
  const map = {
    pending: 'border-info/40 bg-info/10 text-info',
    accepted: 'border-accent-primary/40 bg-accent-primary/10 text-accent-primary',
    rejected: 'border-red-500/40 bg-red-500/10 text-red-400'
  }
  return map[status] || 'border-white/10 bg-white/5 text-text-secondary'
}

export const treasuryTypeLabel = (type, t, award, capture, receivedAt) => {
  if (type === 'capture_award' && capture) {
    const started = receivedAt - capture.start_ago * 1000
    const time = (timestamp) => new Intl.DateTimeFormat(undefined, { timeZone: 'Europe/Moscow', hourCycle: 'h23', hour: '2-digit', minute: '2-digit' }).format(timestamp)
    return t('team.treasuryType.captureAwardDetails', {
      mode: modeLabel(capture.mode, t),
      start: time(started),
      end: time(started + 900000)
    })
  }
  if (type === 'top_award' && award) {
    const date = typeof award.date === 'string' ? award.date.split('-').reverse().slice(0, 2).join('.') : award.date
    return t('team.treasuryType.topAwardDetails', 'Приз за {{place}} место в топе {{mode}} за {{date}}', {
      place: award.place,
      mode: modeLabel(award.mode, t),
      date
    })
  }
  const labels = {
    admin_add: t('team.treasuryType.adminAdd', 'Начисление администрацией'),
    admin_sub: t('team.treasuryType.adminSub', 'Списание администрацией'),
    pay: t('team.treasuryType.pay', 'Выплата участнику'),
    disband: t('team.treasuryType.disband', 'Остаток при роспуске'),
    burn: t('team.treasuryType.burn', 'Казна сгорела'),
    top_award: t('team.treasuryType.topAward', 'Приз за топ'),
    capture_award: t('team.treasuryType.captureAward'),
    // Награды лестницы и призы розыгрыша эвента; перевод — в ресурсах эвента
    halloween_award: t('halloweenEvent.treasuryAward')
  }
  return labels[type] || type
}

// ---------------------------------------------------------------------------
// Имена и аудит
// ---------------------------------------------------------------------------

/** Имя игрока из блока user/actor/target; null (удалён из базы или админ) — свой фолбэк */
export const personName = (person, t, fallback) => {
  if (!person) return fallback ?? t('team.system', 'Администрация')
  const name = `${person.firstname || ''} ${person.lastname || ''}`.trim()
  return name || t('team.player', 'Игрок')
}

/**
 * Фраза аудит-лога: бэкенд отдаёт только ключ действия, участников и meta.
 * Возвращает готовый текст без имени актора — имя рисуется отдельной
 * кликабельной ссылкой на профиль (ProfileLink), поэтому фраза начинается
 * с глагола: «выплатил Игроку 1200 кристаллов».
 */
export const auditActionText = (event, t) => {
  const meta = event?.meta || {}
  const target = personName(event?.target, t, t('team.someone', 'участника'))

  switch (event?.action) {
    case 'create':
      return t('team.audit.create', 'создал банду «{{name}}»', { name: meta.name || '' })
    case 'join':
      return t('team.audit.join', 'принял {{name}} в банду', { name: target })
    case 'leave':
      return t('team.audit.leave', 'вышел из банды')
    case 'kick':
      return t('team.audit.kick', 'исключил {{name}}', { name: target })
    case 'role':
      return t('team.audit.role', 'сменил роль {{name}}: {{from}} → {{to}}', {
        name: target,
        from: roleLabel(meta.from, t),
        to: roleLabel(meta.to, t)
      })
    case 'transfer':
      return t('team.audit.transfer', 'передал права создателя {{name}}', { name: target })
    case 'pay':
      return t('team.audit.pay', 'выплатил {{name}} {{amount}} кристаллов', {
        name: target,
        amount: meta.amount ?? 0
      })
    case 'application_reject':
      return t('team.audit.applicationReject', 'отклонил заявку {{name}}', { name: target })
    case 'settings':
      return t('team.audit.settings', 'изменил настройки банды')
    case 'announcement':
      return meta.empty
        ? t('team.audit.announcementCleared', 'убрал объявление лидера')
        : t('team.audit.announcement', 'обновил объявление лидера')
    case 'colors':
      return t('team.audit.colors', 'изменил цвета банды')
    case 'avatar':
      return t('team.audit.avatar', 'загрузил новый аватар банды')
    case 'background':
      return t('team.audit.background', 'загрузил новый фон банды')
    case 'delete_start':
      return t('team.audit.deleteStart', 'запустил удаление банды')
    case 'delete_cancel':
      return t('team.audit.deleteCancel', 'отменил удаление банды')
    case 'disband':
      return t('team.audit.disband', 'Банда распущена, остаток казны ({{treasury}}) ушёл создателю', {
        treasury: meta.treasury ?? 0
      })
    case 'admin_treasury':
      return Number(meta.amount) < 0
        ? t('team.audit.adminTreasurySub', 'Администрация списала {{amount}} кристаллов из казны', { amount: Math.abs(Number(meta.amount) || 0) })
        : t('team.audit.adminTreasuryAdd', 'Администрация начислила {{amount}} кристаллов в казну', { amount: Math.abs(Number(meta.amount) || 0) })
    case 'admin_kick':
      return t('team.audit.adminKick', 'Администрация исключила {{name}}', { name: target })
    case 'admin_freeze':
      return t('team.audit.adminFreeze', 'Администрация заморозила банду')
    case 'admin_unfreeze':
      return t('team.audit.adminUnfreeze', 'Администрация разморозила банду')
    case 'admin_delete':
      return t('team.audit.adminDelete', 'Администрация удалила банду (казна сгорела: {{treasury}})', {
        treasury: meta.treasury_burned ?? 0
      })
    case 'admin_texts':
      return t('team.audit.adminTexts', 'Администрация поправила тексты банды')
    case 'admin_colors':
      return t('team.audit.adminColors', 'Администрация сбросила цвета банды')
    case 'admin_images':
      return t('team.audit.adminImages', 'Администрация сбросила картинки банды')
    case 'admin_max_members':
      return t('team.audit.adminMaxMembers', 'Потолок состава изменён на {{count}}', {
        count: meta.max_members ?? 0
      })
    case 'admin_images_ban':
      return meta.banned
        ? t('team.audit.adminImagesBan', 'Администрация запретила банде загрузку картинок')
        : t('team.audit.adminImagesUnban', 'Администрация вернула банде загрузку картинок')
    default:
      return event?.action || ''
  }
}

// ---------------------------------------------------------------------------
// Ошибки
// ---------------------------------------------------------------------------

/**
 * Текст ошибки по коду бэкенда. `retry_after` — секунды до следующей попытки
 * (кулдауны создания, смены названия, повторной заявки).
 */
export const teamErrorText = (code, t, extra = {}) => {
  const retryAfter = Number(extra?.retry_after)
  const minutes = Number.isFinite(retryAfter) ? Math.max(1, Math.ceil(retryAfter / 60)) : null

  const messages = {
    // Создание
    already_in_team: t('team.err.alreadyInTeam', 'Ты уже состоишь в банде'),
    deposit_required: t('team.err.depositRequired', 'Нужно хотя бы одно пополнение баланса'),
    recreate_cooldown: minutes
      ? t('team.err.recreateCooldownIn', 'Новую банду можно создать через {{count}} мин', { count: minutes })
      : t('team.err.recreateCooldown', 'Новую банду можно создать позже'),
    // Суточный лимит создания: ждать бывает много часов, поэтому не минуты, а компактный формат
    create_daily_limit: Number.isFinite(retryAfter)
      ? t('team.err.createDailyLimitIn', 'Больше 3 банд в сутки создать нельзя — попробуй через {{time}}', { time: formatSecondsLeft(retryAfter, t) })
      : t('team.err.createDailyLimit', 'Больше 3 банд в сутки создать нельзя'),
    invalid_name: t('team.err.invalidName', 'Название от 3 до 24 символов'),
    name_too_plain: t('team.err.nameTooPlain', 'В названии нужно минимум 3 буквы или цифры'),
    name_taken: t('team.err.nameTaken', 'Такое название уже занято'),
    name_cooldown: minutes
      ? t('team.err.nameCooldownIn', 'Название можно менять раз в 5 минут — попробуй через {{count}} мин', { count: minutes })
      : t('team.err.nameCooldown', 'Название можно менять раз в 5 минут'),

    // Заявки
    not_recruiting: t('team.err.notRecruiting', 'Банда сейчас не набирает участников'),
    team_frozen: t('team.err.teamFrozen', 'Банда заморожена администрацией'),
    team_deleting: t('team.err.teamDeleting', 'Банда удаляется — действие недоступно'),
    too_many_applications: t('team.err.tooManyApplications', 'Больше 5 активных заявок нельзя'),
    application_exists: t('team.err.applicationExists', 'Заявка в эту банду уже отправлена'),
    apply_cooldown: minutes
      ? t('team.err.applyCooldownIn', 'Повторная заявка в эту банду — через {{count}} мин', { count: minutes })
      : t('team.err.applyCooldown', 'Повторная заявка возможна раз в 5 минут'),
    too_long_text: t('team.err.tooLongText', 'Текст заявки — до 300 символов'),
    application_closed: t('team.err.applicationClosed', 'Заявку уже обработали'),
    team_full: t('team.err.teamFull', 'В банде нет свободных мест'),

    // Состав
    forbidden: t('team.err.forbidden', 'Недостаточно прав'),
    not_member: t('team.err.notMember', 'Игрок не в банде'),
    self_kick: t('team.err.selfKick', 'Себя исключить нельзя'),
    self_role: t('team.err.selfRole', 'Свою роль менять нельзя'),
    same_role: t('team.err.sameRole', 'Эта роль уже стоит'),
    invalid_role: t('team.err.invalidRole', 'Неизвестная роль'),
    confirm_required: t('team.err.confirmRequired', 'Нужно подтверждение'),
    invalid_target: t('team.err.invalidTarget', 'Игрок не найден'),
    role_changed: t('team.err.roleChanged', 'Роль уже изменил кто-то другой — обнови страницу'),
    owner_cannot_leave: t('team.err.ownerCannotLeave', 'Сначала передай права создателя'),

    // Казна
    invalid_amount: t('team.err.invalidAmount', 'Неверная сумма'),
    not_enough_treasury: t('team.err.notEnoughTreasury', 'В казне недостаточно кристаллов'),
    not_in_team: t('team.err.notInTeam', 'Ты не состоишь в банде'),

    // Тексты и настройки
    nothing_to_update: t('team.err.nothingToUpdate', 'Нечего сохранять'),
    too_long_description: t('team.err.tooLongDescription', 'Описание — до 500 символов'),
    too_long_recruit_text: t('team.err.tooLongRecruitText', 'Текст набора — до 300 символов'),
    too_long_announcement: t('team.err.tooLongAnnouncement', 'Объявление — до 500 символов'),
    invalid_contact_chat: t('team.err.invalidContactChat', 'Ссылка на чат — только Telegram или Discord'),
    invalid_contact_channel: t('team.err.invalidContactChannel', 'Ссылка на канал — только Telegram или Discord'),

    // Голоса
    never_member: t('team.err.neverMember', 'Голосовать могут те, кто состоит или состоял в банде'),
    vote_frozen: t('team.err.voteFrozen', 'Голос заморожен: прошли сутки после выхода из банды'),
    invalid_value: t('team.err.invalidValue', 'Неверное значение'),

    // Картинки
    no_file: t('team.err.noFile', 'Файл не выбран'),
    invalid_format: t('team.err.invalidFormat', 'Подходят только JPG и PNG'),
    invalid_image: t('team.err.invalidImage', 'Не удалось прочитать картинку'),
    image_too_big: t('team.err.imageTooBig', 'Картинка слишком большая'),
    file_too_large: t('team.err.fileTooLarge', 'Файл больше 1 МБ'),
    rate_limit: t('team.err.rateLimit', 'Лимит загрузок на сегодня исчерпан'),
    images_banned: t('team.err.imagesBanned', 'Банде запрещена загрузка картинок'),
    images_disabled: t('team.err.imagesDisabled', 'Загрузка картинок временно отключена'),
    cdn_error: t('team.err.cdnError', 'Хранилище недоступно, попробуй позже'),

    // Прочее
    query_too_short: t('team.err.queryTooShort', 'Введи минимум 2 символа'),
    already_deleting: t('team.err.alreadyDeleting', 'Удаление уже запущено'),
    not_deleting: t('team.err.notDeleting', 'Удаление не запускалось'),
    not_found: t('team.err.notFound', 'Банда не найдена')
  }

  return messages[code] || t('team.err.default', 'Что-то пошло не так, попробуй ещё раз')
}

// ---------------------------------------------------------------------------
// Тексты и лимиты
// ---------------------------------------------------------------------------

/**
 * Длина текста в графемах: бэкенд считает эмодзи за один символ, а
 * String.length — за 2–8, иначе счётчик под полем разойдётся с сервером.
 */
export const countGraphemes = (text) => {
  const value = text || ''
  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    try {
      return [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(value)].length
    } catch (e) {
      // ниже фолбэк по кодовым точкам
    }
  }
  // Фолбэк: кодовые точки со склейкой ZWJ-последовательностей и вариационных селекторов
  return [...value].filter((char) => char !== '\u200D' && !/[\uFE00-\uFE0F]/.test(char)).length
}

/** Минимум 3 буквы или цифры в названии — та же проверка, что на бэкенде */
export const hasEnoughAlphanumeric = (name) => {
  const matches = (name || '').match(/[\p{L}\p{N}]/gu)
  return (matches ? matches.length : 0) >= 3
}

const CONTACT_HOSTS = ['t.me', 'telegram.me', 'telegram.dog', 'discord.gg', 'discord.com', 'discordapp.com']

/**
 * Подсказка перед отправкой: принимаются только ссылки на Telegram и Discord,
 * либо @username (бэкенд сам приведёт его к https://t.me/username).
 * Финальную валидацию всё равно делает сервер.
 */
export const isAcceptableContact = (value) => {
  const text = (value || '').trim()
  if (!text) return true
  if (/^@[\w\d_]{3,}$/.test(text)) return true
  try {
    const url = new URL(text.startsWith('http') ? text : `https://${text}`)
    const host = url.hostname.replace(/^www\./, '').toLowerCase()
    return CONTACT_HOSTS.includes(host)
  } catch (e) {
    return false
  }
}

/** Секунды до роспуска из unixtime `delete.deletes_at` (единственное поле-таймстамп в API банд) */
export const secondsUntil = (unixtime) => {
  if (!Number.isFinite(Number(unixtime))) return 0
  return Math.max(0, Math.round(Number(unixtime) - Date.now() / 1000))
}
